/* *
 * Hero Carousel - js/components/hero-carousel.js
 * Slide 1: hero content + images
 * Slide 2: hero content + stats + live reviews (no hardcoded fallbacks)
 *
 * Reviews: GET /api/reviews/public/featured - only live data shown. */

function _apiBase() {
  const h = window.location.hostname;
  const isDev = h === 'localhost' || h === '127.0.0.1' || h === '' || h.startsWith('192.168.') || location.protocol === 'file:';
  return isDev ? 'http://localhost:5001' : 'https://api.ascenddraft.com';
}
const REVIEWS_ENDPOINT = _apiBase() + "/api/reviews/public/featured";
const REVIEW_INTERVAL_MS = 4500;
const SLIDE_AUTO_ADVANCE_MS = 8000;

let track, prevBtn, nextBtn, dots, counterCurrent, counterTotal;
let currentSlide = 0;
let totalSlides = 3;
let slideTimer = null;
let reviewTimer = null;
let currentReview = 0;
let allReviews = [];
let _paused = false;
let _heartbeatBtn = null;

/* SVG star - avoids Unicode encoding corruption */
function _starSvg() {
  return '<svg class="hero-review-stars__star" width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>';
}

/* Shrink/grow .hero-carousel to match the active slide's own content height */
function syncHeroHeight() {
  const carousel = document.querySelector('.hero-carousel');
  if (!carousel || !track) return;
  const slides = track.querySelectorAll('.hero-carousel__slide');
  const slide = slides[currentSlide];
  if (!slide) return;
  const h = slide.scrollHeight;
  const minH = window.innerWidth <= 575 ? 300 : window.innerWidth <= 1023 ? 400 : 480;
  carousel.style.transition = 'none';
  carousel.style.minHeight = '0';
  carousel.style.height = Math.max(h, minH) + 'px';
  carousel.offsetHeight; // force reflow before restoring transitions
  carousel.style.transition = '';
}

/* Carousel logic */
function goToSlide(index) {
  const clamped = ((index % totalSlides) + totalSlides) % totalSlides;
  currentSlide = clamped;
  track.style.transform = "translateX(-" + (currentSlide * 100) + "%)";
  dots.forEach((d, i) => {
    d.classList.toggle("hero-carousel__dot--active", i === currentSlide);
    d.setAttribute("aria-selected", i === currentSlide ? "true" : "false");
  });
  if (counterCurrent) counterCurrent.textContent = String(currentSlide + 1).padStart(2, "0");
  syncHeroHeight();
  resetSlideTimer();
}

function resetSlideTimer() {
  if (_paused) return;
  if (slideTimer) clearTimeout(slideTimer);
  slideTimer = setTimeout(() => goToSlide(currentSlide + 1), SLIDE_AUTO_ADVANCE_MS);
}

function pauseCarousel() {
  _paused = true;
  if (slideTimer) { clearTimeout(slideTimer); slideTimer = null; }
  if (_heartbeatBtn) { _heartbeatBtn.classList.add('hb-paused'); _heartbeatBtn.setAttribute('aria-label', 'Resume slideshow'); _heartbeatBtn.title = 'Click to resume'; }
}

function resumeCarousel() {
  _paused = false;
  resetSlideTimer();
  if (_heartbeatBtn) { _heartbeatBtn.classList.remove('hb-paused'); _heartbeatBtn.setAttribute('aria-label', 'Pause slideshow'); _heartbeatBtn.title = 'Click to pause'; }
}

/* Reviews */
function renderReview(index) {
  const review = allReviews[index];
  if (!review) return;
  const slot = document.querySelector(".hero-review-slot");
  if (!slot) return;

  const existing = slot.querySelector(".hero-review-item--active");
  if (existing) {
    existing.classList.remove("hero-review-item--active");
    existing.classList.add("hero-review-item--exit");
    setTimeout(() => existing.remove(), 500);
  }

  const rating = review.rating || 5;
  const starsHtml = Array.from({length: Math.min(rating, 5)}, _starSvg).join("");

  const item = document.createElement("div");
  item.className = "hero-review-item";
  item.innerHTML =
    '<div>' +
    '<div class="hero-review-stars" aria-label="' + rating + ' out of 5 stars">' + starsHtml + '</div>' +
    '<p class="hero-review-text">' + escapeHtml(stripHtml(review.text)) + '</p>' +
    '</div>' +
    '<p class="hero-review-attribution">&#8212; ' + escapeHtml(review.author) + '</p>';
  slot.appendChild(item);
  requestAnimationFrame(() => item.classList.add("hero-review-item--active"));
  updateReviewDots(index);
}

function updateReviewDots(index) {
  const container = document.querySelector(".hero-review-dots");
  if (!container) return;
  if (container.children.length !== allReviews.length) {
    container.innerHTML = "";
    allReviews.forEach((_, i) => {
      const btn = document.createElement("button");
      btn.className = "hero-review-dot";
      btn.setAttribute("aria-label", "Review " + (i + 1));
      btn.addEventListener("click", () => { currentReview = i; renderReview(i); resetReviewTimer(); });
      container.appendChild(btn);
    });
  }
  Array.from(container.children).forEach((d, i) => {
    d.classList.toggle("hero-review-dot--active", i === index);
  });
}

function advanceReview() {
  if (!allReviews.length) return;
  currentReview = (currentReview + 1) % allReviews.length;
  renderReview(currentReview);
}

function resetReviewTimer() {
  if (reviewTimer) clearInterval(reviewTimer);
  reviewTimer = setInterval(advanceReview, REVIEW_INTERVAL_MS);
}

/* Unified identity - platform display name, not real name */
function _resolveIdentity(r) {
  return (r.author && (r.author.displayName || r.author.username || r.author.nickname || r.author.identityTag || r.author.handle)) || "Verified Client";
}

/* Fetch live reviews only */
async function fetchReviews() {
  const slot = document.querySelector(".hero-review-slot");
  try {
    const res = await fetch(REVIEWS_ENDPOINT);
    if (!res.ok) throw new Error("HTTP " + res.status);
    const data = await res.json();
    const raw = (data && data.data && data.data.reviews) || (data && data.reviews) || [];
    const mapped = raw
      .map(r => ({ text: stripHtml(r.content || r.comment || r.text || ""), author: _resolveIdentity(r), rating: r.rating || 5 }))
      .filter(r => r.text.trim().length > 3);
    if (mapped.length > 0) {
      allReviews = mapped;
      currentReview = 0;
      renderReview(0);
      resetReviewTimer();
    }
    // No data from API -> keep slot empty, no fake fallbacks
  } catch (_) {
    if (slot) slot.style.display = "none";
  }
}

/* Escape HTML */
function escapeHtml(str) {
  return String(str || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/* Strip HTML tags to extract plain text */
function stripHtml(str) {
  return String(str || "").replace(/<[^>]*>/g, "").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&nbsp;/g, " ").trim();
}

/* Init */
function initHeroCarousel() {
  track = document.querySelector(".hero-carousel__track");
  prevBtn = document.querySelector(".hero-carousel__nav--prev");
  nextBtn = document.querySelector(".hero-carousel__nav--next");
  dots = Array.from(document.querySelectorAll(".hero-carousel__dot"));
  counterCurrent = document.querySelector(".hero-carousel__counter-current");
  counterTotal = document.querySelector(".hero-carousel__counter-total");
  if (!track) return;

  totalSlides = document.querySelectorAll(".hero-carousel__slide").length;
  if (prevBtn) prevBtn.addEventListener("click", () => goToSlide(currentSlide - 1));
  if (nextBtn) nextBtn.addEventListener("click", () => goToSlide(currentSlide + 1));
  dots.forEach((dot, i) => dot.addEventListener("click", () => goToSlide(i)));
  if (counterTotal) counterTotal.textContent = String(totalSlides).padStart(2, "0");

  let touchStartX = 0;
  track.addEventListener("touchstart", e => { touchStartX = e.touches[0].clientX; }, {passive: true});
  track.addEventListener("touchend", e => {
    const dx = touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(dx) > 40) goToSlide(currentSlide + (dx > 0 ? 1 : -1));
  }, {passive: true});
  document.addEventListener("keydown", e => {
    if (e.key === "ArrowLeft") goToSlide(currentSlide - 1);
    if (e.key === "ArrowRight") goToSlide(currentSlide + 1);
  });

  goToSlide(0);
  resetSlideTimer();
  fetchReviews();
  syncHeroHeight();
  window.addEventListener('resize', syncHeroHeight, { passive: true });

  // Inject heartbeat pause button
  const heroWrap = document.querySelector(".hero-carousel") || document.querySelector(".hero");
  if (heroWrap && !heroWrap.querySelector('.hb-pause-btn')) {
    _heartbeatBtn = document.createElement('button');
    _heartbeatBtn.className = 'hb-pause-btn';
    _heartbeatBtn.setAttribute('aria-label', 'Pause slideshow');
    _heartbeatBtn.title = 'Click to pause';
    _heartbeatBtn.innerHTML = '<span class="hb-ring"></span><span class="hb-icon hb-icon--pause"><span></span><span></span></span><span class="hb-icon hb-icon--play">&#9654;</span>';
    _heartbeatBtn.addEventListener('click', e => { e.stopPropagation(); _paused ? resumeCarousel() : pauseCarousel(); });
    heroWrap.appendChild(_heartbeatBtn);
    // Tap-to-pause on hero banner (opt-out via data-no-tap-pause attribute)
    if (!heroWrap.hasAttribute('data-no-tap-pause')) {
      heroWrap.addEventListener('click', e => {
        if (e.target.closest('.hb-pause-btn') || e.target.closest('a') || e.target.closest('button')) return;
        _paused ? resumeCarousel() : pauseCarousel();
      });
    }
  }

  const heroSection = document.querySelector(".hero");
  if (heroSection && "IntersectionObserver" in window) {
    new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (!_paused && !slideTimer) resetSlideTimer();
          if (!reviewTimer && allReviews.length) resetReviewTimer();
        } else {
          if (slideTimer) { clearTimeout(slideTimer); slideTimer = null; }
          if (reviewTimer) { clearInterval(reviewTimer); reviewTimer = null; }
        }
      });
    }, {threshold: 0.15}).observe(heroSection);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initHeroCarousel);
} else {
  initHeroCarousel();
}
